<template>
  <!--
    Pages such as Users, Assessments, and Home have more than one root (the page plus dialogs).
    A transition on that component never finishes, so every later tab stays blank until refresh.
    The wrapper is one element, and duration does not wait on a transitionend that may never fire.
  -->
  <transition name="page-fade" mode="out-in" :duration="160">
    <div
      v-if="dynamicPage"
      :key="renderedKey"
      class="page-slot"
    >
      <component
        :is="dynamicPage.is"
        v-bind="dynamicPage.props"
      />
    </div>
  </transition>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import { useAuthentication } from '@/stores/auth'
import { useLayout } from '@/stores/layout'
import { min } from 'lodash'

const authenticationStore = useAuthentication()
const layoutStore = useLayout()

const { isInstructor, isAdmin, isDeveloper, isLearner, getCurrentRole } = storeToRefs(authenticationStore)

const { page, isReloadingPage } = storeToRefs(layoutStore)

const components = shallowRef(null)
const dynamicPage = shallowRef(null)
const dynamicPageKey = ref(0)
const renderedKey = ref('')
let pageRequest = 0

const showPage = (view) => {
  dynamicPage.value = view
  renderedKey.value = `${page.value?.value || 'home'}-${dynamicPageKey.value}`
}

const CHUNK_RELOAD_KEY = 'prp_chunk_reload'

const isStaleChunkError = (error) => {
  const message = String(error?.message || '')
  return (
    message.startsWith('Failed to fetch dynamically imported module:') ||
    message.startsWith('error loading dynamically imported module:') ||
    message.includes('Importing a module script failed')
  )
}

const reloadOnceForStaleChunk = (componentPath) => {
  try {
    const key = `${CHUNK_RELOAD_KEY}:${componentPath}`
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
    window.location.reload()
    return true
  } catch {
    return false
  }
}

const clearStaleChunkFlag = (componentPath) => {
  try {
    sessionStorage.removeItem(`${CHUNK_RELOAD_KEY}:${componentPath}`)
  } catch {
    // ignore storage errors
  }
}

const setDynamicPage = async () => {
  const requestId = ++pageRequest
  let componentPath = ''
  try {
    const componentName = page.value == null || page.value?.name == null ? 'Home' : page.value.name
    componentPath =
      page.value == null || page.value?.common == null || page.value.common == true

        ? `../components/${componentName}.vue`

        : `../components/${getCurrentRole.value.code.trim().toLowerCase()}/${componentName}.vue`
    if (!components.value[componentPath])
      throw new Error(`Unknown variable dynamic import: ${componentPath}`)
    const module = await components.value[componentPath]()
    clearStaleChunkFlag(componentPath)
    if (requestId !== pageRequest) return
    const targetComponent = module.default
    const requiredRoles = targetComponent?.roleAccess
    if (requiredRoles != null && !requiredRoles.includes(getCurrentRole.value.code)) {
      const empty = markRaw((await import('../components/reusables/EmptyState.vue')).default)
      if (requestId !== pageRequest) return
      showPage({ is: empty })
    } else if (requestId === pageRequest) {
      showPage({
        is: markRaw(targetComponent),
        props: page.value?.props
      })
    }
  } catch (e) {
    if (isStaleChunkError(e) && reloadOnceForStaleChunk(componentPath))
      return
    if (
      e.toString().startsWith('Error: Unknown variable dynamic import:') ||
      isStaleChunkError(e)
    ) {
      try {
        const empty = markRaw((await import('../components/reusables/EmptyState.vue')).default)
        if (requestId !== pageRequest) return
        showPage({ is: empty })
      } catch (e) {
        if (e.toString().startsWith('Error: Unknown variable dynamic import:')) {
          authenticationStore.router.push('/404')
          if (page.value != null) page.value = null
        } else throw e
      }
    } else throw e
  }
}


watch([page, isAdmin, isInstructor, isDeveloper, isReloadingPage, isLearner], (c, o) => {
  // Same menu item clicked again (layout store sets isReloadingPage).
  const isReloadTrigger = c[0]?.value == o[0]?.value && c[1] == o[1] && c[2] == o[2] && c[3] == o[3] && c[4] != o[4]
  // if explained:
  // Load the main panel when the user has a portal role AND either:
  //   (1) a menu page is selected (c[0]) and we're not only toggling reload on the same item, or
  //   (2) they clicked the same nav item again to force a refresh (isReloadingPage + isReloadTrigger).
  // Uses c[0] (new page), not o[0] (old page), so the first sidebar click still loads content.
  if (((c[0] != null && !isReloadTrigger) || (c[4] && isReloadTrigger)) && (c[1] || c[2] || c[3] || c[5])) {
    if (c[4] && isReloadTrigger) dynamicPageKey.value++
    setDynamicPage()
    if (c[4] && isReloadTrigger) isReloadingPage.value = false
  }
})

onBeforeMount(() => {
  components.value = import.meta.glob('../components/**/*.vue')
  if (isAdmin.value || isInstructor.value || isDeveloper.value || isLearner.value)
    setDynamicPage()
  dynamicPageKey.value = 0
})
</script>
